/**
 * docSnippet — the hand-off behind the docs' "Open in editor" buttons.
 *
 * A runnable code block in the AstroTour[AR] reference stashes its source under a one-shot
 * `localStorage` key and links to `/?snippet=<id>` with `target="_blank"`. `IDEView` consumes
 * the key on mount and seeds the new sketch with it.
 *
 * Why `localStorage` and not the URL:
 *   - snippets run well past a comfortable URL length, and
 *   - `sessionStorage` inheritance into a link-opened tab is browser-dependent, whereas
 *     `localStorage` is shared across tabs of the same origin.
 *
 * The new tab is the point: the doc stays open beside the editor and an unsaved sketch in the
 * current tab is never clobbered.
 */

export interface DocSnippet {
  code: string;
  title?: string;
  /** ms epoch; entries older than {@link MAX_AGE_MS} are swept on the next stash. */
  savedAt: number;
}

const PREFIX = 'stemarts.docSnippet.';
const MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

/** Unguessable-enough id, same idiom as `client/firebase/arSession.js`. */
export function makeSnippetId(): string {
  const c = (typeof window !== 'undefined' && window.crypto) || undefined;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID().replace(/-/g, '');
  }
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (e) {
    // Safari private mode and friends throw on access rather than returning null.
    return null;
  }
}

/**
 * Drop snippets a stale tab never opened, so a student who browses the docs for an hour
 * doesn't leave a pile of dead keys behind.
 */
function sweep(store: Storage) {
  const now = Date.now();
  const doomed: string[] = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (key && key.startsWith(PREFIX)) {
      try {
        const entry = JSON.parse(store.getItem(key) || '{}');
        if (!entry.savedAt || now - entry.savedAt > MAX_AGE_MS)
          doomed.push(key);
      } catch (e) {
        doomed.push(key);
      }
    }
  }
  doomed.forEach((key) => store.removeItem(key));
}

/** Store `code` under `id`. Silently no-ops when storage is unavailable or full. */
export function stashSnippet(id: string, code: string, title?: string): void {
  const store = storage();
  if (!store) return;
  try {
    sweep(store);
    const entry: DocSnippet = { code, title, savedAt: Date.now() };
    store.setItem(PREFIX + id, JSON.stringify(entry));
  } catch (e) {
    // Quota or private-mode failure: the editor just opens an empty sketch instead.
  }
}

/** Read and delete the snippet for `id`. Returns null when absent or unreadable. */
export function consumeSnippet(id: string): DocSnippet | null {
  const store = storage();
  if (!store) return null;
  const key = PREFIX + id;
  let raw: string | null = null;
  try {
    raw = store.getItem(key);
    store.removeItem(key);
  } catch (e) {
    return null;
  }
  if (!raw) return null;
  try {
    const entry = JSON.parse(raw);
    return typeof entry?.code === 'string' ? (entry as DocSnippet) : null;
  } catch (e) {
    return null;
  }
}

/** The editor URL that consumes `id`. */
export function snippetEditorUrl(id: string): string {
  return `/?snippet=${encodeURIComponent(id)}`;
}
